import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, ExternalLink, X } from 'lucide-react';

type LightboxState = {
  src: string;
  alt: string;
  title?: string;
};

type ThumbnailLightboxContextValue = {
  openLightbox: (state: LightboxState) => void;
};

const ThumbnailLightboxContext = createContext<ThumbnailLightboxContextValue | null>(null);

export function ThumbnailLightboxProvider({ children }: { children: ReactNode }) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [contain, setContain] = useState(true);

  const openLightbox = useCallback((state: LightboxState) => {
    setContain(true);
    setLightbox(state);
  }, []);

  const contextValue = useMemo(() => ({ openLightbox }), [openLightbox]);

  return (
    <ThumbnailLightboxContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={!!lightbox}
        onOpenChange={(open) => {
          if (!open) setLightbox(null);
        }}
      >
        <DialogContent className="max-w-5xl p-0 bg-black flex flex-col items-center">
          {lightbox && (
            <>
              <DialogHeader className="flex items-center justify-between px-3 py-2 w-full">
                <div className="flex flex-col">
                  <DialogTitle className="text-sm text-white/90">
                    {lightbox.title || lightbox.alt}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-white/60">
                    Click image to toggle fit
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={lightbox.src}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs bg-white/10 hover:bg-white/15 text-white transition"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open
                  </a>
                  <a
                    href={lightbox.src}
                    download
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs bg-white/10 hover:bg-white/15 text-white transition"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                  <DialogClose
                    className="ml-1 inline-flex items-center justify-center rounded-md p-2 hover:bg-white/10 text-white transition"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </DialogClose>
                </div>
              </DialogHeader>
              <div className="relative max-h-[80vh] w-full flex items-center justify-center">
                <img
                  src={lightbox.src}
                  alt={lightbox.alt}
                  loading="lazy"
                  decoding="async"
                  onClick={() => setContain((value) => !value)}
                  className={`select-none cursor-zoom-in ${
                    contain
                      ? 'max-h-[80vh] w-auto object-contain'
                      : 'max-h-[80vh] w-full object-cover'
                  }`}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ThumbnailLightboxContext.Provider>
  );
}

export function useThumbnailLightbox() {
  return useContext(ThumbnailLightboxContext);
}
